import { preFillApplication } from '@/adapter/applicationLoader';
import BlockApi from '@/api/api';
import SingleButtonDialog from '@/components/common/dialogs/SingleButtonDialog';
import NewBlockDialog from '@/components/navigation/NewBlockDialog';
import LoadApplicationDialog from '@/components/navigation/wizard/load/LoadApplicationDialog';
import ApplicationManagementDialog from '@/components/navigation/wizard/management/ApplicationManagementDialog';
import PublishApplicationDialog from '@/components/navigation/wizard/publish/PublishApplicationDialog';
import SaveApplicationDialog from '@/components/navigation/wizard/save/SaveApplicationDialog';
import FileExport from '@/components/project/FileExport';
import { useApplication } from '@/hooks/useApplication';
import { useConfig } from '@/hooks/useConfig';
import { useDialogs } from '@/hooks/useDialogs';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useMetadata } from '@/hooks/useMetadata';
import { useSubroutine } from '@/hooks/useSubroutine';
import { useUpdateNodes } from '@/hooks/useUpdateNodes';
import { useViewportContext } from '@/hooks/useViewport';
import { BackendApplication } from '@/types/backend';
import { emptyApplication, newApplication } from '@/util/application';
import { handleFileChange } from '@/util/file';
import { migrateApplication } from '@/util/migration/migration';
import { isApplication } from '@/util/typeGuards';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PublicIcon from '@mui/icons-material/Public';
import SaveIcon from '@mui/icons-material/Save';
import { Button, Divider, ListItemIcon, Menu, MenuItem } from '@mui/material';
import { ConfirmOptions } from 'material-ui-confirm';
import React, { FC, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocation } from 'react-router';
import AuthorizedMenuItem from './menu-items/AuthorizedMenuItem';

type FileMenuProps = {
  closeDetails: () => void;
};

const FileMenu: FC<FileMenuProps> = (props) => {
  const { setApplication, setBackendApplication, hasUnsavedChanges, setHasUnsavedChanges } = useApplication();
  const { setUpdateNodes } = useUpdateNodes();
  const { showError, showProgress, closeProgress, showTable, confirm } = useDialogs();
  const { metadata } = useMetadata();
  const { config } = useConfig();
  const { setErrors } = useErrorHandler();
  const { setViewports } = useViewportContext();
  const { pathname, search } = useLocation();
  const auth = useAuth();
  const { clearSubroutineHistory, selectedSubroutineId, setSelectedSubroutineId } = useSubroutine();
  const [recentApplications, setRecentApplications] = useState<BackendApplication[]>();
  const [openBlockWizard, setOpenBlockWizard] = useState(false);
  const [openLoadWizard, setOpenLoadWizard] = useState(false);
  const [openSaveWizard, setOpenSaveWizard] = useState(false);
  const [openPublishWizard, setOpenPublishWizard] = useState(false);
  const [openManagementWizard, setOpenManagementWizard] = useState(false);
  const [openUnauthenticated, setOpenUnauthenticated] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const confirmUnsaved: ConfirmOptions = {
    title: 'Unsaved changes',
    description:
      'You have unsaved changes. Do you want to discard them and continue, or cancel to review your changes?',
    confirmationText: 'Discard & Continue',
    cancellationButtonProps: { color: 'secondary' }
  };

  const loadApplication = async (app: BackendApplication) => {
    if (!auth.user?.access_token || !app.applicationId) return;

    showProgress('Loading Application...');
    BlockApi(config.apiUrl, auth.user.access_token)
      .application.load(app.applicationId)
      .then(
        (result) => {
          if (result && result.blockData && isApplication(JSON.parse(result.blockData))) {
            console.info('Success! Application loaded successfully', result);
            const { blockData, ...backendApp } = result;
            const newApp = migrateApplication(JSON.parse(blockData), metadata, showTable);
            const prefilledApp = preFillApplication(newApp, metadata);

            resetValues();
            setSelectedSubroutineId(undefined);
            setApplication(
              {
                ...prefilledApp,
                applicationId: backendApp.applicationId,
                revision: backendApp.revision
              },
              true
            );
            setBackendApplication({ ...backendApp, companyId: auth.user?.profile.company_id as number });
            loadLatest(5);
          } else {
            showError({
              title: 'An error occured',
              message: 'The retreived object is not a valid application',
              stacktrace: new Error(JSON.stringify(result))
            });
          }
          setHasUnsavedChanges(false);
          validate();
          closeProgress();
          props.closeDetails();
          handleClose();
        },
        (error) => {
          showError({
            message: error.message,
            stacktrace: error
          });
        }
      )
      .catch((error: Error) => {
        showError({
          title: 'Could not open the application',
          message: error.message,
          stacktrace: error
        });
        closeProgress();
      });
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const resetValues = () => {
    setErrors([]);
    clearSubroutineHistory();
    setViewports({});
  };

  const handleUnauthenticated = () => {
    setOpenUnauthenticated(true);
    handleClose();
  };

  const validate = () => {
    setUpdateNodes(['*']);
  };

  const handleLoad = () => {
    if (hasUnsavedChanges) {
      confirm(confirmUnsaved).then((result) => {
        if (!result.confirmed) return;
        setOpenLoadWizard(true);
        handleClose();
      });
    } else {
      setOpenLoadWizard(true);
      handleClose();
    }
  };

  const handleSaveApp = () => {
    setOpenSaveWizard(true);
    handleClose();
  };

  const handleExport = () => {
    setOpenExport(true);
    handleClose();
  };

  const handleApplicationManagement = () => {
    setOpenManagementWizard(true);
    handleClose();
  };

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (hasUnsavedChanges) {
      confirm(confirmUnsaved).then((result) => {
        if (result.confirmed) handleFileChanges(event);
      });
    } else {
      handleFileChanges(event);
    }
  };

  const handleFileChanges = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileChange(
      event,
      (app) => {
        if (app && isApplication(app)) {
          // Migrate before touching any state, so a file that cannot be opened leaves the current one alone.
          const newApp = migrateApplication(app, metadata, showTable);
          resetValues();
          setSelectedSubroutineId(undefined);
          setBackendApplication(undefined);

          const { applicationId, ...cleanApplication } = preFillApplication(newApp, metadata);
          setApplication({ ...cleanApplication, generated: '' }, true);
          props.closeDetails();
        }
        validate();
        handleClose();
      },
      (error) => {
        showError({
          title: 'Could not open the application file',
          message: error.message,
          stacktrace: error
        });
      }
    );
  };

  const handleCloseApplication = () => {
    if (hasUnsavedChanges) {
      confirm(confirmUnsaved).then((result) => {
        if (!result.confirmed) return;
        clearProject();
        setHasUnsavedChanges(false);
      });
    } else {
      clearProject();
      setHasUnsavedChanges(false);
    }
    handleClose();
  };

  const clearProject = () => {
    setApplication(emptyApplication, true);
    setBackendApplication(undefined);
    setSelectedSubroutineId(undefined);
    resetValues();
    validate();
    props.closeDetails();
  };

  const startNewApplication = () => {
    setApplication(newApplication, true);
    setBackendApplication(undefined);
    resetValues();
    validate();
    handleClose();
  };

  const handleNew = () => {
    if (hasUnsavedChanges) {
      confirm(confirmUnsaved).then((result) => {
        if (!result.confirmed) return;
        startNewApplication();
        setHasUnsavedChanges(false);
        props.closeDetails();
      });
    } else {
      startNewApplication();
      setHasUnsavedChanges(false);
      props.closeDetails();
    }
    handleClose();
  };

  const handlePublishApp = () => {
    if (hasUnsavedChanges) {
      confirm(confirmUnsaved).then((result) => {
        if (!result.confirmed) return;
        setOpenPublishWizard(true);
        handleClose();
      });
    } else {
      setOpenPublishWizard(true);
      handleClose();
    }
  };

  const loadLatest = (amount: number) => {
    if (!auth.user || !auth.isAuthenticated) return;

    BlockApi(config.apiUrl, auth.user.access_token)
      .application.latest(amount)
      .then(
        (result) => !!result?.length && setRecentApplications(result),
        (error) =>
          showError({
            title: 'An error has occurred while loading latest used applications',
            message: error.message,
            stacktrace: error
          })
      );
  };

  useEffect(() => loadLatest(5), [auth.user]);

  return (
    <>
      <Button
        id='file-button'
        aria-controls={open ? 'file-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        color='inherit'
      >
        File
      </Button>
      <Menu
        id='file-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'file-button',
          sx: { minWidth: '10rem' }
        }}
      >
        <MenuItem onClick={handleNew}>
          <ListItemIcon>
            <AddIcon fontSize='small' />
          </ListItemIcon>
          New
        </MenuItem>
        <AuthorizedMenuItem onClick={auth.isAuthenticated ? handleLoad : handleUnauthenticated}>
          <ListItemIcon>
            <FolderOpenIcon fontSize='small' />
          </ListItemIcon>
          Load
        </AuthorizedMenuItem>
        <Divider />
        <AuthorizedMenuItem
          disabled={!selectedSubroutineId}
          onClick={auth.isAuthenticated ? handleSaveApp : handleUnauthenticated}
        >
          <ListItemIcon>
            <SaveIcon fontSize='small' />
          </ListItemIcon>
          Save
        </AuthorizedMenuItem>
        <Divider />
        <AuthorizedMenuItem
          disabled={!selectedSubroutineId}
          onClick={auth.isAuthenticated ? handlePublishApp : handleUnauthenticated}
        >
          <ListItemIcon>
            <PublicIcon fontSize='small' />
          </ListItemIcon>
          Publish
        </AuthorizedMenuItem>
        <Divider />
        <MenuItem component='label'>
          <ListItemIcon>
            <FileUploadIcon fontSize='small' />
          </ListItemIcon>
          Import
          <input hidden accept='.json,application/json' type='file' onChange={handleFileImport} />
        </MenuItem>
        <MenuItem disabled={!selectedSubroutineId} onClick={handleExport}>
          <ListItemIcon>
            <FileDownloadIcon fontSize='small' />
          </ListItemIcon>
          Export
        </MenuItem>
        <Divider />
        <AuthorizedMenuItem onClick={auth.isAuthenticated ? handleApplicationManagement : handleUnauthenticated}>
          <ListItemIcon>
            <LibraryBooksIcon fontSize='small' />
          </ListItemIcon>
          Application Management
        </AuthorizedMenuItem>
        {recentApplications?.length && <Divider />}
        {recentApplications?.map((app) => (
          <MenuItem key={app.applicationId} onClick={() => loadApplication(app)}>
            <ListItemIcon>
              <FolderOpenIcon fontSize='small' />
            </ListItemIcon>
            {app.label}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem disabled={!selectedSubroutineId} onClick={handleCloseApplication}>
          <ListItemIcon>
            <CloseIcon fontSize='small' />
          </ListItemIcon>
          Close
        </MenuItem>
      </Menu>
      <FileExport open={openExport} onClose={() => setOpenExport(false)} />
      <NewBlockDialog open={openBlockWizard} onClose={() => setOpenBlockWizard(false)} />
      <LoadApplicationDialog
        open={openLoadWizard}
        onClose={() => setOpenLoadWizard(false)}
        loadApplication={loadApplication}
      />
      <SaveApplicationDialog
        open={openSaveWizard}
        onClose={() => setOpenSaveWizard(false)}
        setRecentApplications={(result: BackendApplication[]) => setRecentApplications(result)}
      />
      <PublishApplicationDialog open={openPublishWizard} onClose={() => setOpenPublishWizard(false)} />
      <ApplicationManagementDialog
        open={openManagementWizard}
        onClose={() => {
          setOpenManagementWizard(false);
          loadLatest(5);
        }}
      />
      <SingleButtonDialog
        open={openUnauthenticated}
        onClose={() => setOpenUnauthenticated(false)}
        message={'You are not logged in'}
        confirmLabel={'Log In'}
        onConfirm={() => {
          sessionStorage.setItem('authRedirectUrl', `${pathname}${search}`);
          auth.signinRedirect();
        }}
      />
    </>
  );
};

export default FileMenu;
